import { observer } from 'mobx-react-lite';
import { FC, ReactElement } from 'react';
import { Controller } from 'react-hook-form';

import { useTranslation } from '@gem/modules/i18n';
import { Button, ButtonTypes, TextField } from '@gem/ui-kit';
import { patterns } from '@gem/utils';

import { UnauthorizedLayoutTemplate } from 'APP/components/UnauthorizedLayoutTemplate/UnauthorizedLayoutTemplate';
import { requiredValidation } from 'APP/packages/form/validation/requiredValidation';
import { getUniqueAliasValidation } from 'APP/pages/authorization/Login/LoginViews/LoginAddNewServerStep/validations/uniqueAliasValidation';

import { LoginRegionSupportInfo } from '../../../components/LoginRegionSupportInfo/LoginRegionSupportInfo';
import {
  BASE_URL_EXAMPLE,
  BASE_URL_PROTOCOL_STRING,
  ILoginAddNewServerStepFormData,
  ILoginAddNewServerStepProps,
  useLoginAddNewServerStepPresenter,
} from './LoginAddNewServerStep.presenter';
import styles from './LoginAddNewServerStep.styles.m.css';

export const LoginAddNewServerStep: FC<ILoginAddNewServerStepProps> = observer((props) => {
  const { loginPresenter } = props;
  const { t } = useTranslation();
  const { presenter, form } = useLoginAddNewServerStepPresenter(props);

  return (
    <UnauthorizedLayoutTemplate
      onBack={presenter.onBackHandler || undefined}
      title={t('auth_connect_company_title')}
      subtitle={t('auth_connect_company_subtitle')}
      info={<LoginRegionSupportInfo />}
      message={loginPresenter.errorMessage}
    >
      <div className={styles.content}>
        <form
          className={styles.form}
          onSubmit={presenter.onSubmit}
        >
          <Controller
            control={form.control}
            name="baseUrl"
            rules={{
              ...requiredValidation<ILoginAddNewServerStepFormData>(),
              pattern: {
                value: patterns.compile(patterns.WEB_URL_WITHOUT_PROTOCOL),
                message: t('common_invalid_url'),
              },
            }}
            render={({ field, fieldState }): ReactElement => (
              <TextField
                prefix={BASE_URL_PROTOCOL_STRING}
                placeholder={BASE_URL_EXAMPLE}
                className={styles.formControl}
                {...field}
                onChange={presenter.onBaseUrlChange}
                isDisabled={form.formState.isSubmitting || field.disabled}
                ref={null}
                hasError={fieldState.invalid}
                message={fieldState.error?.message || ' '}
                onBlur={(): void => {
                  field.onBlur();
                  presenter.normalizeUrl();
                }}
              />
            )}
          />
          <Controller
            control={form.control}
            name="alias"
            rules={{
              ...requiredValidation<ILoginAddNewServerStepFormData>(),
              ...getUniqueAliasValidation<ILoginAddNewServerStepFormData>(loginPresenter.email),
            }}
            render={({ field, fieldState }): ReactElement => (
              <TextField
                placeholder={t('auth_company_server_alias_label')}
                className={styles.formControl}
                {...field}
                isDisabled={form.formState.isSubmitting || field.disabled}
                ref={null}
                hasError={fieldState.invalid}
                message={fieldState.error?.message || t('auth_company_server_alias_description')}
              />
            )}
          />
          <Button
            className={styles.submit}
            type={ButtonTypes.Submit}
            theme="primary"
            size="20"
            isLoading={form.formState.isSubmitting}
            isDisabled={!form.formState.isValid}
          >
            {t('auth_continue')}
          </Button>
        </form>
      </div>
    </UnauthorizedLayoutTemplate>
  );
});
